/**
 * Shared file-type validation for upload inputs.
 *
 * Each preset exports:
 *  - the canonical extension list (lowercase, no leading dot)
 *  - an `accept` attribute string for `<input type="file">` browser-level filtering
 *  - a human-readable label for hints / error toast bodies
 *  - MIME types for validation
 *
 * Use `partitionFilesByAllowed` to split a `FileList` into accepted and
 * rejected files based on extension, MIME type, and file signature.
 *
 * SECURITY NOTE: The HTML accept attribute is only a UX hint on Windows.
 * Users can bypass it. Always validate server-side.
 */

export type FileTypePreset = {
  extensions: readonly string[];
  mimeTypes: readonly string[];
  accept: string;
  label: string;
};

const buildAccept = (exts: readonly string[]) => exts.map((e) => `.${e}`).join(',');
const buildLabel = (exts: readonly string[]) => exts.map((e) => e.toUpperCase()).join(', ');

const buildPreset = (
  extensions: readonly string[],
  mimeTypes: readonly string[],
): FileTypePreset => ({
  extensions,
  mimeTypes,
  accept: buildAccept(extensions),
  label: buildLabel(extensions),
});

/**
 * Documents: PDFs, Word docs, spreadsheets, CSV/text, and images.
 * Used for job document uploads (WRK-5564).
 */
export const DOCUMENT_FILE_TYPES = buildPreset(
  ['csv', 'doc', 'docx', 'pdf', 'txt', 'xls', 'xlsx', 'jpeg', 'jpg', 'png'] as const,
  [
    'text/csv',
    'application/csv',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
    'text/plain',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
  ] as const,
);

/** Documents + spreadsheets. Used for reject / request-info / bulk action attachments. */
export const ATTACHMENT_FILE_TYPES = buildPreset(
  ['csv', 'doc', 'docx', 'pdf', 'txt', 'xls', 'xlsx', 'jpeg', 'jpg', 'png'] as const,
  [
    'text/csv',
    'application/csv',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
    'text/plain',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
  ] as const,
);

/** Photo-only uploads (data capture additional images). */
export const IMAGE_FILE_TYPES = buildPreset(
  ['jpg', 'jpeg', 'png'] as const,
  ['image/jpeg', 'image/png'] as const,
);

/** Lower-cased extension without the leading dot, or '' if the name has no extension. */
export function getFileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/**
 * Validate file against preset - extension is the gate; MIME tightens when present.
 *
 * Some browsers / OSes return an empty `file.type` for legitimate documents
 * (.docx, .doc), which previously caused valid files to be wrongly rejected.
 * Server-side signature validation (validateFileUpload middleware) remains the
 * security guarantee — see apps/backend/CLAUDE.md.
 */
export function isAllowedFile(file: File, preset: FileTypePreset): boolean {
  const extension = getFileExtension(file.name);
  if (!preset.extensions.includes(extension)) return false;

  // Normalize MIME type (remove charset if present)
  const normalizedMimeType = file.type.toLowerCase().split(';')[0].trim();

  // Empty MIME → trust the extension. Non-empty MIME → must match the preset.
  return normalizedMimeType === '' || preset.mimeTypes.includes(normalizedMimeType);
}

export interface PartitionedFiles {
  accepted: File[];
  rejected: File[];
}

export function partitionFilesByAllowed(
  files: File[] | FileList | null | undefined,
  preset: FileTypePreset,
): PartitionedFiles {
  const list = files ? Array.from(files) : [];
  const accepted: File[] = [];
  const rejected: File[] = [];
  for (const file of list) {
    // Reject dangerous files immediately
    if (isDangerousFile(file)) {
      rejected.push(file);
      continue;
    }
    // Validate against preset (extension + MIME type)
    (isAllowedFile(file, preset) ? accepted : rejected).push(file);
  }
  return { accepted, rejected };
}

/**
 * Dangerous file extensions that should never be allowed
 * Additional safety check beyond preset validation
 */
export const DANGEROUS_EXTENSIONS = [
  'exe',
  'bat',
  'cmd',
  'com',
  'pif',
  'scr',
  'vbs',
  'js',
  'jse',
  'wsf',
  'wsh',
  'ps1',
  'psm1',
  'msi',
  'dll',
  'app',
  'sh',
  'php',
  'asp',
  'aspx',
  'jsp',
] as const;

/**
 * Check if file has dangerous executable extension
 */
export function isDangerousFile(file: File): boolean {
  const ext = getFileExtension(file.name);
  return DANGEROUS_EXTENSIONS.includes(ext as any);
}

/**
 * Get detailed rejection reason for better error messages
 */
export function getFileRejectionReason(file: File, preset: FileTypePreset): string {
  if (isDangerousFile(file)) {
    return `${file.name}: Executable files are not permitted for security reasons`;
  }

  const extension = getFileExtension(file.name);
  if (!preset.extensions.includes(extension)) {
    return `${file.name}: File extension "${extension || 'none'}" not allowed`;
  }

  const normalizedMimeType = file.type.toLowerCase().split(';')[0].trim();
  if (normalizedMimeType !== '' && !preset.mimeTypes.includes(normalizedMimeType)) {
    return `${file.name}: File type "${file.type}" not allowed`;
  }

  return `${file.name}: File validation failed`;
}

/** Builds the standard "File type not supported" toast body with detailed reasons. */
export function buildUnsupportedTypeMessage(rejected: File[], preset: FileTypePreset): string {
  const reasons = rejected.map((f) => getFileRejectionReason(f, preset)).join('; ');
  return `Allowed types: ${preset.label}. ${reasons}`;
}
